import DriveSidebar from 'components/layout/DriveSidebar'
import { useDispatch, useSelector } from 'react-redux'
import React, { useState } from 'react'
import Page from 'rui/widgets/Page'
import { Helmet } from 'react-helmet'
import { Box, Container, Spacer, Text, Toggle } from 'roadie-ui'
import Panel, { PanelTitle } from 'components/layout/Panel'
import { DrivingProfile, Flags } from 'types/profile'
import { setPrivacySettings } from 'actions/privacySettings'
import { useLocalization, LocalizedText } from 'localization'

const PrivacySettingsPage: React.FC = () => {
  const { t } = useLocalization()
  const isWebView = useSelector<any, boolean>((state) => state.config.isWebview)
  const isMobile = useSelector<any, boolean>((state) => state.config.mediaSize === 'mobile')
  const profile = useSelector<any, DrivingProfile>((state) => state.profile)
  const flags = useSelector<any, Flags>((state) => state.flags[0])

  const [isTargetedAdvertisingOptOut, setIsTargetedAdvertisingOptOut] = useState<boolean>(
    Boolean(flags.targeted_advertising_opt_out)
  )
  const [isDNSSOptOut, setIsDNSSOptOut] = useState<boolean>(Boolean(flags.dnss_opt_out))
  const dispatch = useDispatch()

  const handleTargetedAdvertisingOptOutToggle = async () => {
    try {
      setPrivacySettings(profile.id, { targeted_advertising_opt_out: !isTargetedAdvertisingOptOut })(dispatch)
      setIsTargetedAdvertisingOptOut(!isTargetedAdvertisingOptOut)
    } catch (err) {
      setIsTargetedAdvertisingOptOut(Boolean(flags.targeted_advertising_opt_out))
      dispatch({
        type: 'PUSH_FLASH',
        flash: {
          messageKey: 'dweb_flash_privacy_setting_update_problem',
          type: 'error',
        },
      })
    }
  }

  const handleDNSSOptOutToggle = async () => {
    try {
      setPrivacySettings(profile.id, { dnss_opt_out: !isDNSSOptOut })(dispatch)
      setIsDNSSOptOut(!isDNSSOptOut)
    } catch (err) {
      setIsDNSSOptOut(Boolean(flags.dnss_opt_out))
      dispatch({
        type: 'PUSH_FLASH',
        flash: {
          messageKey: 'dweb_flash_privacy_setting_update_problem',
          type: 'error',
        },
      })
    }
  }

  return (
    <Page sidebar={<DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_layout_drive_sidebar_privacy_settings')}</title>
      </Helmet>
      {!isWebView && (
        <PanelTitle>
          <LocalizedText translationKey="dweb_layout_drive_sidebar_privacy_settings" />
        </PanelTitle>
      )}
      <Panel left noPadding>
        <Container.Grid $padding="3rem 2rem" $gridTemplateColumns="1fr max-content" $alignItems="center">
          <Container.GridItem>
            <Text.Subtitle1 $fontWeight="medium" $color="neutral-black">
              <LocalizedText translationKey="dweb_account_privacy_settings_opt_out_targeted_advertising" />
            </Text.Subtitle1>
            <Spacer $height="0.7rem" />
            <Text.Caption>
              <LocalizedText translationKey="dweb_account_privacy_settings_roadie_may_not_use_disclose_my_personal_information" />
            </Text.Caption>
          </Container.GridItem>
          <Container.GridItem $padding="0 0 0 2rem">
            <Container.Base $height="2rem" $width="3rem">
              <Toggle value={isTargetedAdvertisingOptOut} onChange={handleTargetedAdvertisingOptOutToggle} />
            </Container.Base>
          </Container.GridItem>
        </Container.Grid>
        <Box.Border $borderTopWidth="1px" $borderTopStyle="solid" $borderTopColor="neutral-light" />
        <Container.Grid $padding="3rem 2rem" $gridTemplateColumns="1fr max-content" $alignItems="center">
          <Container.GridItem>
            <Text.Subtitle1 $fontWeight="medium" $color="neutral-black">
              <LocalizedText translationKey="dweb_account_privacy_settings_opt_out_data_sales_sharing" />
            </Text.Subtitle1>
            <Spacer $height="0.7rem" />
            <Text.Caption>
              <LocalizedText translationKey="dweb_account_privacy_settings_roadie_may_not_sell_share_my_personal_information" />
            </Text.Caption>
          </Container.GridItem>
          <Container.GridItem $padding="0 0 0 2rem">
            <Container.Base $height="2rem" $width="3rem">
              <Toggle value={isDNSSOptOut} onChange={handleDNSSOptOutToggle} />
            </Container.Base>
          </Container.GridItem>
        </Container.Grid>
      </Panel>
      <Spacer $height="1rem" />
      <Disclaimer isMobile={isMobile} />
    </Page>
  )
}

const Disclaimer: React.FC<{
  isMobile: boolean
}> = (props) => {
  const { isMobile } = props
  return isMobile ? (
    <Container.Grid $padding="0 1.5rem 0 2rem">
      <Text.Subtitle1 $fontSize="1.3rem">
        <LocalizedText translationKey="dweb_privacy_settings_no_personal_info_sale_notice" />
      </Text.Subtitle1>
    </Container.Grid>
  ) : (
    <Text.Subtitle1 $fontSize="1.3rem">
      <LocalizedText translationKey="dweb_privacy_settings_no_personal_info_sale_notice" />
    </Text.Subtitle1>
  )
}

export default PrivacySettingsPage
